import React, { useState, useEffect } from "react"
import { manageDeviceUpdate } from "../../../../api/manage_device_update"
import { getManageEstateList } from "../../../../api/manage_estate_list"
import { ManageDeviceData } from "../"
import { ManageEstateData } from "../../estate/"
import { useNavigate } from "react-router-dom"
import { useLoading } from "../../../LoadingContext"

import Card from "@mui/material/Card"
import MDBox from "components/MDBox"
import MDTypography from "components/MDTypography"
import MDInput from "components/MDInput"
import MDButton from "components/MDButton"

interface ManageDeviceUpdateProps {
  onClose: (num: number) => void // モーダルを閉じるためのコールバック
  deviceData: ManageDeviceData // 編集するサービス提供元データをプロパティとして受け取る
}

const ManageDeviceUpdate: React.FC<ManageDeviceUpdateProps> = ({ onClose, deviceData }) => {
  const { startLoading, stopLoading } = useLoading()
  const navigate = useNavigate()
  const [estateData, setEstateData] = useState<ManageEstateData[]>([])
  const [formData, setFormData] = useState({
    id: deviceData.id,
    serialNo: deviceData.serial_no,
    estateId: "Estate|" + deviceData.estate_id
  })

  const [errors, setErrors] = useState({
    id: "",
    serialNo: "",
    estateId: "",
    server: ""
  })

  const fetchManageEstateList = async () => {
    try {
      startLoading()
      const manageEstateData = await getManageEstateList()
      setEstateData(manageEstateData.estate_list)

      // データ取得後に formData の estateId を更新
      let estateId = deviceData.estate_id ?? ""
      if (estateId != "") {
        estateId = "Estate|" + estateId
      }
      setFormData((prev) => ({
        ...prev,
        estateId: estateId
      }))
    } catch (error: any) {
      if (error.response && error.response.status === 401) {
        navigate("/login") // セッション切れ時にトップページへリダイレクト
      }
    } finally {
      stopLoading()
    }
  }

  useEffect(() => {
    fetchManageEstateList()
  }, [])

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => {
    const target = e.target as HTMLInputElement // 明示的に型キャスト
    const { name, value, type, checked } = target
    setFormData((prev) => ({
      ...prev,
      [name]: type === "checkbox" ? checked : value
    }))
  }

  const validate = () => {
    const validationErrors = { ...errors }
    let hasError = false

    // 英数字のみを許可する正規表現
    const alphanumericRegex = /^[a-zA-Z0-9]+$/

    // 必須項目のバリデーション
    if (!formData.serialNo) {
      validationErrors.serialNo = "必須項目が入力されていません"
      hasError = true
    } else if (!alphanumericRegex.test(formData.serialNo)) {
      validationErrors.serialNo = "英数字のみ使用できます"
      hasError = true
    } else if (formData.serialNo.length !== 12) {
      validationErrors.serialNo = "入力文字数は12文字です"
      hasError = true
    } else {
      validationErrors.serialNo = ""
    }

    validationErrors.server = ""

    setErrors(validationErrors)
    return !hasError
  }

  const convertEnabledValues = (data: any) => {
    return data
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    if (validate()) {
      // バリデーション成功時の処理
      try {
        startLoading()
        const convertedData = convertEnabledValues(formData)
        await manageDeviceUpdate(convertedData.id, convertedData.serialNo, convertedData.estateId)
        onClose(1)
      } catch (error: any) {
        if (error.response && error.response.status === 401) {
          navigate("/login") // セッション切れ時にトップページへリダイレクト
        } else if (error.response) {
          const errorDetail = error.response.data.detail
          const validationErrors = {
            id: "",
            serialNo: "",
            estateId: "",
            server: ""
          }
          validationErrors.server = errorDetail.message
          setErrors(validationErrors)
        } else {
          const validationErrors = {
            id: "",
            serialNo: "",
            estateId: "",
            server: ""
          }
          validationErrors.server = "サーバーへの接続に失敗しました"
          setErrors(validationErrors)
        }
      } finally {
        stopLoading()
      }
    }
  }

  return (
    <div className="modal-bg-cover">
      <Card
        sx={{
          width: "90%",
          maxWidth: "500px",
          maxHeight: "calc(100vh - 100px)",
          marginTop: "50px",
          marginBottom: "50px",
          paddingTop: "20px",
          paddingBottom: "20px",
          paddingLeft: "20px"
        }}
      >
        <div
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            width: "100%",
            padding: "8px 16px",
            boxSizing: "border-box"
          }}
        >
          <MDTypography variant="h4" fontWeight="medium" style={{ flex: 1, textAlign: "left" }}>
            デバイスを更新
          </MDTypography>
          <button
            className="close-button"
            onClick={() => onClose(0)}
            style={{
              position: "absolute",
              right: 16,
              top: 8,
              background: "transparent",
              border: "none",
              fontSize: "24px",
              cursor: "pointer"
            }}
          >
            ×
          </button>
        </div>
        <MDBox style={{ height: "20px" }}></MDBox>
        <MDBox
          style={{
            maxHeight: "calc(100vh - 200px)",
            overflowY: "auto",
            paddingTop: "10px",
            paddingLeft: "5px",
            paddingRight: "25px"
          }}
        >
          <MDBox display="flex" alignItems="center" gap={1} mb={2}>
            <MDInput
              label="デバイスID"
              type="text"
              name="id"
              value={formData.id}
              onChange={handleChange}
              sx={{ width: "100%" }}
              inputProps={{ readOnly: true }}
            />
          </MDBox>
          <MDBox display="flex" alignItems="center" gap={1} mb={2}>
            <MDInput
              label="シリアルNo"
              type="text"
              name="serialNo"
              value={formData.serialNo}
              onChange={handleChange}
              sx={{ width: "100%" }}
            />
          </MDBox>
          {errors.serialNo && (
            <MDBox
              color="#FF0000" // テキスト色
              pl={2} // 左パディング
              mb={2} // 下マージン
              fontSize="0.7em" // フォントサイズ
            >
              {errors.serialNo}
            </MDBox>
          )}
          <div>
            <select
              name="estateId"
              value={formData.estateId ?? ""}
              className={formData.estateId == "" ? "placeholder" : ""}
              onChange={handleChange}
            >
              <option value="">不動産業者を選択してください</option>
              {estateData.map((estate) => (
                <option key={estate.id} value={estate.id}>
                  {estate.name}
                </option>
              ))}
            </select>
            {errors.estateId && <p className="error-message">{errors.estateId}</p>}
          </div>
          {errors.server && (
            <MDBox
              color="#FF0000" // テキスト色
              pl={2} // 左パディング
              mb={2} // 下マージン
              fontSize="0.7em" // フォントサイズ
            >
              {errors.server}
            </MDBox>
          )}
          <MDBox mt={4} mb={2}>
            <MDButton
              variant="contained"
              color="info"
              fullWidth
              sx={{
                borderRadius: "4px",
                textTransform: "none",
                fontSize: "1rem",
                padding: "0.75rem 0",
                backgroundColor: "#1976d2",
                "&:hover": {
                  backgroundColor: "#1565c0"
                }
              }}
              onClick={(e) => {
                handleSubmit(e)
              }}
            >
              更新
            </MDButton>
          </MDBox>
        </MDBox>
      </Card>
    </div>
  )
}

export default ManageDeviceUpdate
